/**
 * Animation easing functions.
 * 
 * See type declaration AnimationEasingDeclaration for list of predefined easings
 * 
 * Additional easing function can be added with addEasing(..). This function will also register it
 * with jQuery, if jQuery is available.
 * 
 * Example referencing an easing function, creating a new bezier one, and then using it.
 * 
 * import AnimationEasing from './AnimationEasing';
 * console.log( AnimationEasing.func.easeInBack );
 * 
 * AnimationEasing.addEasing('interaction', AnimationEasing.bezier(.3,1,.8,1));
 * AnimationEasing.addEasing('noInteraction', AnimationEasing.bezier(.4,.69,.5,1));
 * console.log( AnimationEasing.func.interaction );
 * console.log( AnimationEasing.func.noInteraction );
 *
 * The build in easing functions are based on jQueryUI. Documentation (with nice graphs): http://api.jqueryui.com/easings/
 */
import * as $ from 'jquery';

export interface EaseFunc {
	(x:number): number;
};

export interface AnimationEasingDeclaration {
	linear: EaseFunc;
	swing: EaseFunc;
	easeInQuad: EaseFunc;
	easeOutQuad: EaseFunc;
	easeInOutQuad: EaseFunc;
	easeInCubic: EaseFunc;
	easeOutCubic: EaseFunc;
	easeInOutCubic: EaseFunc;
	easeInQuart: EaseFunc;
	easeOutQuart: EaseFunc;
	easeInOutQuart: EaseFunc;
	easeInQuint: EaseFunc;
	easeOutQuint: EaseFunc;
	easeInOutQuint: EaseFunc;
	easeInExpo: EaseFunc;
	easeOutExpo: EaseFunc;
	easeInOutExpo: EaseFunc;
	easeInSine: EaseFunc;
	easeOutSine: EaseFunc;
	easeInOutSine: EaseFunc;
	easeInCirc: EaseFunc;
	easeOutCirc: EaseFunc;
	easeInOutCirc: EaseFunc;
	easeInElastic: EaseFunc;
	easeOutElastic: EaseFunc;
	easeInOutElastic: EaseFunc;
	easeInBack: EaseFunc;
	easeOutBack: EaseFunc;
	easeInOutBack: EaseFunc;
	easeInBounce: EaseFunc;
	easeOutBounce: EaseFunc;
	easeInOutBounce: EaseFunc;
	[key: string]: EaseFunc;
}

function addEasing(name: string, fn: EaseFunc): void {
	// Add to dictionary
	func[name] = fn;

	// Add to jQuery
	(<any>$).easing[name] = fn;
}

/**
 * Function that creates bezier curve
 */
function bezier(mX1:number, mY1:number, mX2:number, mY2:number): EaseFunc {
	if (mX1 == mY1 && mX2 == mY2) {
		return function(aX) { return aX; };
	} else {
		return function(aX) { return CalcBezier(GetTForX(aX), mY1, mY2); };
	}

	function A(aA1:number, aA2:number) {
		return 1.0 - 3.0 * aA2 + 3.0 * aA1;
	}
	function B(aA1:number, aA2:number) {
		return 3.0 * aA2 - 6.0 * aA1;
	}
	function C(aA1:number) {
		return 3.0 * aA1;
	}

	// Returns x(t) given t, x1, and x2, or y(t) given t, y1, and y2.
	function CalcBezier(aT:number, aA1:number, aA2:number) {
		return ((A(aA1, aA2)*aT + B(aA1, aA2))*aT + C(aA1))*aT;
	}

	// Returns dx/dt given t, x1, and x2, or dy/dt given t, y1, and y2.
	function GetSlope(aT:number, aA1:number, aA2:number) {
		return 3.0 * A(aA1, aA2)*aT*aT + 2.0 * B(aA1, aA2) * aT + C(aA1);
	}

	// Newton raphson iteration
	function GetTForX(aX:number): number {
		var aGuessT = aX;
		for (var i = 0; i < 4; ++i) {
			var currentSlope = GetSlope(aGuessT, mX1, mX2);
			if (currentSlope == 0.0)
				return aGuessT;
			var currentX = CalcBezier(aGuessT, mX1, mX2) - aX;
			aGuessT -= currentX / currentSlope;
		}
		return aGuessT;
	}
}
	
let func: AnimationEasingDeclaration = {
	linear: function(p) {
		return p;
	},
	swing: function(p) {
		return 0.5 - Math.cos( p * Math.PI ) / 2;
	},

	// These functions are dynamically created below.
	easeInQuad: undefined,
	easeOutQuad: undefined,
	easeInOutQuad: undefined,
	easeInCubic: undefined,
	easeOutCubic: undefined,
	easeInOutCubic: undefined,
	easeInQuart: undefined,
	easeOutQuart: undefined,
	easeInOutQuart: undefined,
	easeInQuint: undefined,
	easeOutQuint: undefined,
	easeInOutQuint: undefined,
	easeInExpo: undefined,
	easeOutExpo: undefined,
	easeInOutExpo: undefined,
	easeInSine: undefined,
	easeOutSine: undefined,
	easeInOutSine: undefined,
	easeInCirc: undefined,
	easeOutCirc: undefined,
	easeInOutCirc: undefined,
	easeInElastic: undefined,
	easeOutElastic: undefined,
	easeInOutElastic: undefined,
	easeInBack: undefined,
	easeOutBack: undefined,
	easeInOutBack: undefined,
	easeInBounce: undefined,
	easeOutBounce: undefined,
	easeInOutBounce: undefined
};

export default {
	func, addEasing, bezier
};

// Create an object that contains all base easing functions
let baseEasings:{[key: string]: EaseFunc} = {
	Sine: function(p) {
		return 1 - Math.cos( p * Math.PI / 2 );
	},
	Circ: function(p) {
		return 1 - Math.sqrt( 1 - p * p );
	},
	Elastic: function(p) {
		return p === 0 || p === 1 ? p :
			-Math.pow( 2, 8 * (p - 1) ) * Math.sin( ( (p - 1) * 80 - 7.5 ) * Math.PI / 15 );
	},
	Back: function(p) {
		return p * p * ( 3 * p - 2 );
	},
	Bounce: function(p) {
		var pow2, bounce = 4;
		while ( p < ( ( pow2 = Math.pow( 2, --bounce ) ) - 1 ) / 11 ) {}
		return 1 / Math.pow( 4, 3 - bounce ) - 7.5625 * Math.pow( ( pow2 * 3 - 2 ) / 22 - p, 2 );
	},

	Quad: function(p) { return Math.pow(p, 2); },
	Cubic: function(p) { return Math.pow(p, 3); },
	Quart: function(p) { return Math.pow(p, 4); },
	Quint: function(p) { return Math.pow(p, 5); },
	Expo: function(p) { return Math.pow(p, 6); }
};

// Iterate through the base easing functions and create 'easeIn' 'easeOut' and 'easeInOut' variations
for (let name in baseEasings) {
	if (baseEasings.hasOwnProperty(name)) {
		addVariations(name, baseEasings[name])
	}
}
function addVariations(name: string, easeFn: EaseFunc) {
	addEasing('easeIn' + name, easeFn);
	addEasing('easeOut' + name, function(p) {
		return 1 - easeFn( 1 - p );
	});
	addEasing('easeInOut' + name, function(p) {
		return p < 0.5 ?
				easeFn( p * 2 ) / 2 :
				1 - easeFn( p * -2 + 2 ) / 2;
	});
}